import {
	FeedTank,
	FeedTankGraphItem,
	FeedTankGraphRefillItem,
	FeedTypeLevelMetricEnum,
} from '@/api';

type FeedTankGraphDataItem = FeedTankGraphItem | FeedTankGraphRefillItem;

export class FeedTankGraphData {
	public word: string;
	public value: number;
	public unit: string;
	public text: string;

	constructor(feedTank: FeedTank, item: FeedTankGraphDataItem, isPrimary: boolean) {
		this.word = isPrimary ? this.getPrimaryWord(feedTank) : this.getSecondaryWord(feedTank);
		this.value = isPrimary
			? this.getPrimaryValue(feedTank, item)
			: this.getSecondaryValue(feedTank, item);
		this.unit = isPrimary
			? this.getPrimaryUnit(feedTank, item)
			: this.getSecondaryUnit(feedTank, item);
		this.text = isPrimary
			? this.formatPrimaryValue(feedTank, item)
			: this.formatSecondaryValue(feedTank, item);
	}

	private isWeight(feedTank: FeedTank): boolean {
		return feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight;
	}

	private isTonnes(feedTank: FeedTank, value: number): boolean {
		return this.isWeight(feedTank) && value >= 1000;
	}

	private getPrimaryWord(feedTank: FeedTank) {
		return this.isWeight(feedTank) ? 'Weight' : 'Volume';
	}

	private getSecondaryWord(feedTank: FeedTank) {
		return !this.isWeight(feedTank) ? 'Weight' : 'Volume';
	}

	private getPrimaryValue(feedTank: FeedTank, item: FeedTankGraphDataItem): number {
		const sensorType = feedTank.sensorType ?? 'radar';
		if (sensorType == 'strain')
			return item?.weight ?? item?.value ?? 0;
		const density = feedTank.feedtype?.density ?? 1;
		const inverseDensity = 1 / density;
		const convertedFromVolume = (item?.volume ?? item?.value ?? 0) * density;
		const convertedFromWeight = (item?.weight ?? item?.volumeWeight ?? 0) * inverseDensity;
		return this.isWeight(feedTank)
			? item?.weight ?? item?.volumeWeight ?? convertedFromVolume ?? 0
			: item?.volume ?? item?.value ?? convertedFromWeight ?? 0;
	}

	private getSecondaryValue(feedTank: FeedTank, item: FeedTankGraphDataItem): number {
		const density = feedTank.feedtype?.density ?? 1;
		const inverseDensity = 1 / density;
		const convertedFromVolume = (item?.volume ?? item?.value ?? 0) * density;
		const convertedFromWeight = (item?.weight ?? item?.volumeWeight ?? 0) * inverseDensity;
		return !this.isWeight(feedTank)
			? item?.weight ?? item?.volumeWeight ?? convertedFromVolume ?? 0
			: item?.volume ?? item?.value ?? convertedFromWeight ?? 0;
	}

	private getPrimaryUnit(feedTank: FeedTank, item: FeedTankGraphDataItem): string {
		return this.isWeight(feedTank)
			? this.isTonnes(feedTank, this.getPrimaryValue(feedTank, item))
				? 't'
				: 'kg'
			: 'L';
	}

	private getSecondaryUnit(feedTank: FeedTank, item: FeedTankGraphDataItem): string {
		return !this.isWeight(feedTank)
			? this.isTonnes(feedTank, this.getSecondaryValue(feedTank, item))
				? 't'
				: 'kg'
			: 'L';
	}

	private formatPrimaryValue(feedTank: FeedTank, item: FeedTankGraphDataItem): string {
		return this.isWeight(feedTank) && this.isTonnes(feedTank, this.getPrimaryValue(feedTank, item))
			? (Math.round(this.getPrimaryValue(feedTank, item) * 0.1) * 0.01).toLocaleString()
			: Math.round(this.getPrimaryValue(feedTank, item)).toLocaleString();
	}

	private formatSecondaryValue(feedTank: FeedTank, item: FeedTankGraphDataItem): string {
		return this.isWeight(feedTank)
			? Math.round(this.getSecondaryValue(feedTank, item)).toLocaleString()
			: this.isTonnes(feedTank, this.getSecondaryValue(feedTank, item))
			? (Math.round(this.getSecondaryValue(feedTank, item) * 0.1) * 0.01).toLocaleString()
			: Math.round(this.getSecondaryValue(feedTank, item)).toLocaleString();
	}
}
